// src/components/menu/TopMenu.tsx

import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Dropdown } from "flowbite-react"; // Checkbox foi removido dos imports
import { useNavigate } from "react-router-dom";
import { useCompanyAccessStore } from "../../../states/store/CompanyAccess";
import {
  useCompanyStore,
  useCompanyStoreActions,
} from "../../../states/store/Company";
import MenuHamburguer from "./side-menu/MenuHamburguer";
import { BsBuildingDown, BsBuildingsFill } from "react-icons/bs";
import { GrDocumentConfig } from "react-icons/gr";
import { HiBellAlert } from "react-icons/hi2";
import "./TopMenu.scss";
import { IObjectCompanies } from "../../../../types/companyaccess.type";
import { useUser } from "../../../services/users.api";
import { AuthContext } from "../../auth/authContext/AuthContext";

type RegionName =
  | "SUDESTE"
  | "NORDESTE"
  | "CENTRO-OESTE"
  | "SUL"
  | "NORTE"
  | "OUTRAS";

type CompaniesByRegion = Record<
  string,
  {
    total: number;
    ufs: Record<string, { total: number; cidades: IObjectCompanies[] }>;
  }
>;

const REGION_ORDER: RegionName[] = [
  "SUDESTE",
  "NORDESTE",
  "CENTRO-OESTE",
  "SUL",
  "NORTE",
  "OUTRAS",
];

const UF_TO_REGION: Record<string, RegionName> = {
  AC: "NORTE",
  AL: "NORDESTE",
  AM: "NORTE",
  AP: "NORTE",
  BA: "NORDESTE",
  CE: "NORDESTE",
  DF: "CENTRO-OESTE",
  ES: "SUDESTE",
  GO: "CENTRO-OESTE",
  MA: "NORDESTE",
  MG: "SUDESTE",
  MS: "CENTRO-OESTE",
  MT: "CENTRO-OESTE",
  PA: "NORTE",
  PB: "NORDESTE",
  PE: "NORDESTE",
  PI: "NORDESTE",
  PR: "SUL",
  RJ: "SUDESTE",
  RN: "NORDESTE",
  RO: "NORTE",
  RR: "NORTE",
  RS: "SUL",
  SC: "SUL",
  SE: "NORDESTE",
  SP: "SUDESTE",
  TO: "NORTE",
};

const getRegionByUf = (uf?: string): RegionName => {
  if (!uf) {
    return "OUTRAS";
  }

  return UF_TO_REGION[uf.toUpperCase()] ?? "OUTRAS";
};

function TopMenu() {
  const navigate = useNavigate();
  const { auth, logout } = useContext(AuthContext);
  const { companyAccess } = useCompanyAccessStore();
  const { selectedCompanies, selectedRegion, selectedUf, filterMode } =
    useCompanyStore();
  const {
    toggleCompanySelection,
    setRegionSelection,
    setUfSelection,
    setAllSelection,
  } =
    useCompanyStoreActions();

  const [hasNotification] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [viewProducts, setViewProducts] = useState(false);
  const user = useUser();

  const totalVehicles = useMemo(() => {
    return (
      companyAccess?.companies?.reduce(
        (sum, company) => sum + (company.statistics?.totalVehicles || 0),
        0
      ) ?? 0
    );
  }, [companyAccess]);

  useEffect(() => {
    if (!auth?.user?.roles) return;
    if (auth.user.roles.length > 0) {
      setRoles(auth.user.roles);
      if (
        auth.user.roles.includes("admin") ||
        auth.user.roles.includes("stockist")
      ) {
        setViewProducts(true);
      }
    }
  }, [auth?.user?.roles]);

  const companyAccessByRegion = useMemo(() => {
    const grouped: CompaniesByRegion = {};

    if (Array.isArray(companyAccess?.companies)) {
      companyAccess.companies.forEach((item) => {
        const cityParts = item.companyName?.split("/") ?? [];
        const uf = cityParts[1]?.toUpperCase() ?? "SEM_UF";
        const region = getRegionByUf(uf);
        const total = item?.statistics?.totalVehicles ?? 0;

        if (!grouped[region]) {
          grouped[region] = { total: 0, ufs: {} };
        }

        if (!grouped[region].ufs[uf]) {
          grouped[region].ufs[uf] = { total: 0, cidades: [] };
        }

        grouped[region].ufs[uf].cidades.push(item);
        grouped[region].ufs[uf].total += total;
        grouped[region].total += total;
      });
    }

    return REGION_ORDER.reduce<CompaniesByRegion>((acc, region) => {
      if (grouped[region]) {
        acc[region] = grouped[region];
      }
      return acc;
    }, {});
  }, [companyAccess]);

  const getLocationLabel = () => {
    if (filterMode === "all") {
      return "TODAS AS CIDADES";
    }
    if (filterMode === "region" && selectedRegion) {
      return `TODAS DE ${selectedRegion}`;
    }
    if (filterMode === "uf" && selectedUf) {
      return `TODAS DE ${selectedUf}`;
    }
    if (filterMode === "cities") {
      if (selectedCompanies.length === 1) {
        return selectedCompanies[0].city || "Cidade Selecionada";
      }
      return `MÚLTIPLAS CIDADES (${selectedCompanies.length})`;
    }
    return "Selecione a empresa";
  };

  const isCompanySelected = (companyId: string) => {
    return (
      filterMode === "cities" &&
      selectedCompanies.some((c) => c.companyId === companyId)
    );
  };

  return (
    <header className="top-menu">
      <nav>
        <div className="linha-superior"></div>
        <div className="espacamento"></div>
        <div className="menuhamburguer">
          <MenuHamburguer />
        </div>
        <div className="logo-name">
          <img
            onClick={() => navigate("/")}
            alt="Locavibe logo"
            className="logo-img"
            src="/escudo_vermelho.png"
          />
          <div className="locavibe-backoffice" onClick={() => navigate("/")}>
            <div className="flex justify-center">
              <h2 className="loca ">loca</h2>
              <h2 className="vibe">vibe</h2>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          {viewProducts && (
            <div className="bell-alert">
              <div className="relative group text-center flex">
                <HiBellAlert
                  className={`text-[25px] ${
                    hasNotification
                      ? "text-redPrimary animate-pulse"
                      : "text-white"
                  }`}
                />
                {hasNotification && (
                  <div className="bellAlert-message group-hover:block">
                    Alerta! Peças no estoque abaixo da quantidade mínima!
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="menu-company-names">
            <Dropdown
              className="dropdown-user-info"
              inline
              label={
                <div>
                  <div className="menu-dropdown">{getLocationLabel()}</div>
                  <div className="menu-dropdown-icon">
                    <BsBuildingDown />
                  </div>
                </div>
              }
            >
              {(roles.some((role) => role === "admin") ||
                companyAccess?.viewAll === true) && (
                <Dropdown.Item
                  key="todas-empresas"
                  className="px-1 py-0"
                  onClick={setAllSelection}
                >
                  <div
                    className={`menu-company-list ${
                      filterMode === "all" ? "selected-company" : ""
                    }`}
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div className="flex items-center gap-2">
                      <BsBuildingsFill />
                      <span>TODAS</span>
                    </div>
                    <span>
                      (
                      {new Intl.NumberFormat("pt-BR").format(
                        typeof totalVehicles === "number" ? totalVehicles : 0
                      )}
                      )
                    </span>
                  </div>
                </Dropdown.Item>
              )}
              <div className="dropdown-scroll-container">
                {Object.entries(companyAccessByRegion).map(
                  ([region, { total, ufs }]) => (
                    <Fragment key={region}>
                      <Dropdown.Header
                        className="px-1 py-0 header-company-region"
                        onClick={() => setRegionSelection(region)}
                      >
                        <div
                          className={`menu-company-region ${
                            selectedRegion === region ? "selected-company" : ""
                          }`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{region}</span>
                          <span>
                            ({new Intl.NumberFormat("pt-BR").format(total)})
                          </span>
                        </div>
                      </Dropdown.Header>
                      {Object.entries(ufs).map(([uf, { total, cidades }]) => (
                        <Fragment key={uf}>
                          <Dropdown.Header
                            className={"px-1 py-0 header-company-uf"}
                            onClick={() => setUfSelection(uf)}
                          >
                            <div
                              className={`menu-company-uf ${
                                selectedUf === uf ? "selected-company" : ""
                              }`}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span>{uf}</span>
                              <span>
                                ({new Intl.NumberFormat("pt-BR").format(total)})
                              </span>
                            </div>
                          </Dropdown.Header>
                          {cidades.map((item) => (
                            <Dropdown.Item key={item.companyId} className="p-0">
                              <div
                                className={`w-full px-2 py-1.5 ${
                                  isCompanySelected(item.companyId)
                                    ? "selected-company"
                                    : ""
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCompanySelection(item);
                                }}
                              >
                                <div
                                  className="menu-company-list"
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <span>{item.companyName}</span>
                                  </div>
                                  <span>
                                    (
                                    {new Intl.NumberFormat("pt-BR").format(
                                      item?.statistics?.totalVehicles ?? 0
                                    )}
                                    )
                                  </span>
                                </div>
                              </div>
                            </Dropdown.Item>
                          ))}
                        </Fragment>
                      ))}
                    </Fragment>
                  )
                )}
              </div>
            </Dropdown>
          </div>
          <div className="menu-user-info">
            <Dropdown
              className="dropdown-user-info"
              inline
              label={
                <div>
                  <div className="menu-dropdown">{user.name}</div>
                  <div className="menu-dropdown-icon">
                    <GrDocumentConfig />
                  </div>
                </div>
              }
            >
              <div className="user-info">
                <Dropdown.Header>
                  <div>{user.name}</div>
                  <div>{user.email}</div>
                </Dropdown.Header>
              </div>
              <Dropdown.Item className="pt-0 pb-0">
                <div className="user-config">Minhas configurações</div>
              </Dropdown.Item>
              <Dropdown.Item className="pt-0 pb-0" onClick={() => logout()}>
                <div className="user-config">Sair</div>
              </Dropdown.Item>
            </Dropdown>
          </div>
        </div>
        <div className="espacamento"></div>
      </nav>
    </header>
  );
}

export default TopMenu;
